// axios的封装处理
import axios from 'axios';
import authSession from '@/utils/authSession';
import { logout } from '@/store/modules/user';

// 所有 API 请求直连此地址；更换服务器时在这里修改。
const apiBaseURL = process.env.REACT_APP_API_BASE_URL || 'https://api.instant-partner.cn/api/';
const cookieAuthEnabled = process.env.REACT_APP_COOKIE_AUTH_ENABLED !== 'false';

const requestConfig = {
  baseURL: apiBaseURL,
  timeout: 5000,
  withCredentials: cookieAuthEnabled,
};

const request = axios.create(requestConfig);
const refreshRequest = axios.create(requestConfig);
const csrfRequest = axios.create(requestConfig);

// 远端的 csrftoken 不在当前页面的 document.cookie 中，使用后端返回的 masked token。
const getCsrfHeaders = async () => {
  if (!cookieAuthEnabled) return {};

  const response = await csrfRequest.get('/user/csrf/');
  const csrfToken = response.data?.csrfToken;
  if (!csrfToken) throw new Error('CSRF 接口未返回 csrfToken');

  return { 'X-CSRFToken': csrfToken };
};

let refreshPromise = null;
let refreshGeneration = null;

const requestNewAccessToken = async generation => {
  if (generation !== authSession.getGeneration()) throw new Error('登录会话已变更');
  const headers = await getCsrfHeaders();
  if (generation !== authSession.getGeneration()) throw new Error('登录会话已变更');
  const refreshToken = authSession.getRefreshToken();
  const refreshPayload = refreshToken ? { refresh: refreshToken } : {};
  const response = await refreshRequest.post('/user/token/refresh/', refreshPayload, { headers });
  const newAccessToken = response.data?.access;

  if (!newAccessToken) {
    throw new Error('刷新接口未返回 access token');
  }

  if (generation !== authSession.getGeneration()) throw new Error('登录会话已变更');
  authSession.setAccessToken(newAccessToken);
  window.dispatchEvent(new Event('ipartner-access-refreshed'));

  // 兼容后端开启 refresh token rotation 后返回的新 refresh token。
  if (response.data?.refresh) {
    authSession.setRefreshToken(response.data.refresh);
  }

  return newAccessToken;
};

// 多个请求同时返回 401 时只发起一次刷新，避免 refresh token 重放与竞态。
const refreshAccessToken = () => {
  const generation = authSession.getGeneration();
  if (!refreshPromise || refreshGeneration !== generation) {
    refreshGeneration = generation;
    const pending = (cookieAuthEnabled && window.navigator?.locks?.request
      ? navigator.locks.request('ipartner-refresh-token', () => requestNewAccessToken(generation))
      : requestNewAccessToken(generation))
      .finally(() => {
        if (refreshPromise === pending) refreshPromise = null;
      });
    refreshPromise = pending;
  }

  return refreshPromise;
};

const redirectToLogin = () => {
  if (window.location.pathname !== '/login') window.location.replace('/login');
};

// 在请求发出前从内存读取短期 access token。
request.interceptors.request.use(
  config => {
    config._authGeneration = authSession.getGeneration();
    const accessToken = authSession.getAccessToken();

    if (accessToken) {
      config.headers.Authorization = 'Bearer ' + accessToken;
    }

    return config;
  },
  error => Promise.reject(error)
);

request.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;
    const requestURL = originalRequest?.url || '';
    const isAuthRequest = requestURL.includes('/user/login/')
      || requestURL.includes('/user/token/refresh/')
      || requestURL.includes('/user/logout/');

    if (!originalRequest || isAuthRequest) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (originalRequest._authGeneration !== undefined && originalRequest._authGeneration !== authSession.getGeneration()) return Promise.reject(error);
      const generation = authSession.getGeneration();
      originalRequest._retry = true;
      const store = require('@/store').default;

      try {
        const newAccessToken = await refreshAccessToken();

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = 'Bearer ' + newAccessToken;
        return request(originalRequest);
      } catch (refreshError) {
        if (generation !== authSession.getGeneration()) return Promise.reject(refreshError);
        store.dispatch(logout());
        redirectToLogin();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export { request, refreshAccessToken, getCsrfHeaders, cookieAuthEnabled };
